import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { ProgressBar } from 'primereact/progressbar';
import { Tag } from 'primereact/tag';
import konkrApi from '../../api/konkrApi';
import {
  emptyKonkrForm, fieldsFor, formatKonkrInstant, konkrError, konkrFieldErrors, konkrForm,
  PARTNERSHIPS, partnershipLabel, validateKonkr,
} from '../../utils/konkr';

const actionLabel = { save: 'Save', draft: 'Save Draft', ready: 'Mark Ready', publish: 'Publish', disable: 'Disable',
  activate: 'Activate', deactivate: 'Deactivate', reload: 'Reload Record', close: 'Discard Changes' };

export const KonkrMetadata = ({ record }) => record ? <details className="mt-3">
  <summary>Record and audit metadata</summary>
  <dl className="konkr-metadata">
    <dt>Record ID / revision</dt><dd>{record.id} / {record.revision}</dd>
    <dt>Created</dt><dd>{formatKonkrInstant(record.createdAtUtc)} — {record.createdBy || '\u2014'}</dd>
    <dt>Updated</dt><dd>{formatKonkrInstant(record.updatedAtUtc)} — {record.updatedBy || '\u2014'}</dd>
    {record.publishedAtUtc && <><dt>Published</dt><dd>{formatKonkrInstant(record.publishedAtUtc)} — {record.publishedBy || '\u2014'}</dd></>}
  </dl>
  <small>Account identifiers are recorded IDs, not resolved names. Times shown in Europe/London.</small>
</details> : null;

const KonkrEditor = ({ kind, id, initialAction, advertisers, onClose, onChanged, onAccessError }) => {
  const isOffer = kind === 'offers';
  const [record, setRecord] = useState(null);
  const [form, setForm] = useState(() => emptyKonkrForm(kind));
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!id);
  const [errors, setErrors] = useState({});
  const [feedback, setFeedback] = useState(null);
  const [locked, setLocked] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const mounted = useRef(false);
  const busy = useRef(false);
  const token = useRef(localStorage.getItem('token'));
  const live = () => mounted.current && token.current === localStorage.getItem('token');
  const dirty = JSON.stringify(form) !== JSON.stringify(record ? konkrForm(kind, record) : emptyKonkrForm(kind));
  const selectedAdvertiser = advertisers.find(item => item.id === form.advertiserId);

  const fail = error => {
    if (!live() || onAccessError(error)) return;
    const status = error.response && error.response.status;
    setFeedback({ severity: 'error', text: konkrError(error, 'The Konkr request could not be completed.') });
    setErrors(konkrFieldErrors(error));
    if (!status || status === 409 || status === 404 || status >= 500 || error.code === 'KONKR_RESPONSE_UNVERIFIED') setLocked(true);
  };
  const adopt = latest => { setRecord(latest); setForm(konkrForm(kind, latest)); setLoaded(true); setErrors({}); setLocked(false); };
  const load = async () => {
    if (busy.current) return;
    busy.current = true; setLoading(true); setFeedback(null);
    try {
      const latest = await konkrApi.get(kind, record ? record.id : id);
      if (live()) adopt(latest);
    } catch (error) { fail(error); }
    finally { if (live()) { setLoading(false); busy.current = false; } }
  };
  useEffect(() => {
    mounted.current = true;
    if (id) load();
    return () => { mounted.current = false; };
    // The parent keys the editor by route, authenticated session and record.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!dirty) return undefined;
    const preventLoss = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', preventLoss);
    return () => window.removeEventListener('beforeunload', preventLoss);
  }, [dirty]);

  const requestAction = action => {
    if (busy.current || locked || !loaded) return;
    const validation = validateKonkr(kind, form, { publishing: isOffer && (action === 'publish' ||
      (record && record.status === 'Published' && action !== 'draft' && action !== 'disable')), advertiser: selectedAdvertiser });
    setErrors(validation); setFeedback(null);
    if (Object.keys(validation).length) { setFeedback({ severity: 'warn', text: 'Please correct the highlighted fields.' }); return; }
    const removesApproval = !isOffer && record && record.awinPartnershipStatus === 'Approved' && form.awinPartnershipStatus !== 2;
    if (!['save', 'draft'].includes(action) || removesApproval || (action === 'draft' && record && record.status === 'Published')) {
      setConfirmation({ action, removesApproval });
    } else perform(action);
  };
  const perform = async action => {
    if (busy.current) return;
    setConfirmation(null);
    if (action === 'close') { onClose(); return; }
    if (action === 'reload') { await load(); return; }
    if (locked) return;
    busy.current = true; setSaving(true); setFeedback(null); setErrors({});
    try {
      let current = record;
      if (!current || dirty || action === 'draft') {
        current = await konkrApi.save(kind, form, current, action === 'draft' && Boolean(current));
        if (!live()) return;
        adopt(current); onChanged();
      }
      if (!['save', 'draft'].includes(action)) {
        if (!live()) return;
        current = await konkrApi.action(kind, current.id, action, current.revision);
        if (!live()) return;
        adopt(current); onChanged();
      }
      setFeedback({ severity: 'success', text: `${actionLabel[action]} completed. Server status: ${current.status}.${action === 'ready' ? ' Ready is a review marker, not publishing approval.' : ''}` });
    } catch (error) { fail(error); }
    finally { if (live()) { busy.current = false; setSaving(false); } }
  };
  const close = () => {
    if (busy.current) return;
    if (dirty) setConfirmation({ action: 'close' });
    else onClose();
  };
  const change = (key, value) => setForm(previous => ({ ...previous, [key]: value }));
  const field = (key, label, control, hint) => <div className="field mb-3" key={key}>
    <label htmlFor={`konkr-${key}`}>{label}</label>{control}
    {hint && <small className="d-block text-color-secondary">{hint}</small>}
    {errors[key] && <small id={`konkr-${key}-error`} className="p-error d-block" role="alert">{errors[key]}</small>}
  </div>;
  const disabled = loading || saving || !loaded;
  const advertiserOptions = advertisers.map(item => ({ label: `${item.name} (${item.status}, ${partnershipLabel(item.awinPartnershipStatus)})`, value: item.id }));
  if (record && isOffer && !advertisers.some(item => item.id === record.advertiserId)) {
    advertiserOptions.push({ label: `${record.advertiserName || 'Unavailable advertiser'} (ID ${record.advertiserId})`, value: record.advertiserId });
  }
  return <Dialog header={`${record || id ? 'Edit' : 'Create'} ${isOffer ? 'Offer' : 'Advertiser'}`} visible modal
    style={{ width: '58rem', maxWidth: '96vw' }} draggable={false} closable={!saving} onHide={close}
    footer={<div className="flex flex-wrap gap-2 justify-content-end">
      <Button label="Close" className="p-button-text" disabled={saving} onClick={close} />
      {(record || id) && <Button label="Reload Record" className="p-button-text" disabled={loading || saving}
        onClick={() => setConfirmation({ action: 'reload' })} />}
      <Button label="Save" icon="pi pi-save" disabled={disabled || locked} loading={saving} onClick={() => requestAction('save')} />
      {isOffer ? <>
        <Button label="Save Draft" className="p-button-secondary" disabled={disabled || locked} onClick={() => requestAction('draft')} />
        <Button label="Mark Ready" className="p-button-outlined" disabled={disabled || locked} onClick={() => requestAction('ready')} />
        <Button label="Publish" className="p-button-success" disabled={disabled || locked} onClick={() => requestAction('publish')} />
        <Button label="Disable" className="p-button-danger" disabled={disabled || locked || !record} onClick={() => requestAction('disable')} />
      </> : record && <Button label={record.status === 'Active' ? 'Deactivate' : 'Activate'} className="p-button-outlined"
        disabled={disabled || locked} onClick={() => requestAction(record.status === 'Active' ? 'deactivate' : 'activate')} />}
    </div>}>
    {loading && <ProgressBar mode="indeterminate" style={{ height: '6px' }} />}
    {feedback && <div className="mb-3" aria-live="polite"><Message severity={feedback.severity} text={feedback.text} /></div>}
    {locked && <Message severity="warn" text={record || id ?
      'Changes are locked. Your values are preserved. Reload Record explicitly discards them and loads the current revision; there is no automatic retry.' :
      'Creation outcome is uncertain. Do not submit again. Close and refresh the list to locate the record before creating another one.'} />}
    {!loaded && !loading && <Button label="Retry Record Load" onClick={load} />}
    {loaded && <>
      <div className="flex flex-wrap gap-2 mb-3">
        <Tag value={record ? record.status : isOffer ? 'Draft (new)' : 'Inactive (new)'} />
        {isOffer && record && <Tag value={record.isLive ? 'Live' : 'Not live'} severity={record.isLive ? 'success' : 'warning'} />}
      </div>
      {initialAction && <Message severity="info" text={`Review this record, then use ${actionLabel[initialAction]} below. No lifecycle action runs automatically.`} />}
      {isOffer ? <p>Ready is a review marker only. Publishing requires an Active, Approved advertiser and valid offer details.
        Published does not necessarily mean live; availability is returned by the server.</p> :
        <Message severity="warn" text="Konkr activation and Awin approval are separate. Deactivating or removing approval disables published offers. Reactivating or reapproving does not automatically republish them." />}
      <div className="p-fluid konkr-form mt-3">
        {isOffer && field('advertiserId', 'Advertiser *', <Dropdown inputId="konkr-advertiserId" value={form.advertiserId}
          options={advertiserOptions} filter placeholder="Choose an advertiser" disabled={disabled}
          onChange={event => change('advertiserId', event.value)} aria-invalid={Boolean(errors.advertiserId)} />)}
        {fieldsFor(kind).map(([key, label, max, type]) => field(key,
          `${label}${key === 'name' ? ' *' : ''}`, type === 'text' ?
            <InputTextarea id={`konkr-${key}`} value={form[key]} rows={3} maxLength={max || undefined} disabled={disabled}
              onChange={event => change(key, event.target.value)} aria-invalid={Boolean(errors[key])} /> :
            <InputText id={`konkr-${key}`} value={form[key]} maxLength={max || undefined} disabled={disabled}
              placeholder={type === 'instant' ? '2026-10-20T09:00:00+01:00' : undefined}
              onChange={event => change(key, event.target.value)} aria-invalid={Boolean(errors[key])} />,
          type === 'instant' ? `Optional ISO 8601 timestamp with Z or explicit offset. London display: ${formatKonkrInstant(form[key])}` : undefined))}
        {isOffer ? <>
          {field('pointsRequired', 'Points required *', <InputNumber inputId="konkr-pointsRequired" value={form.pointsRequired}
            min={0} maxFractionDigits={0} useGrouping={false} disabled={disabled} onValueChange={event => change('pointsRequired', event.value)}
            aria-invalid={Boolean(errors.pointsRequired)} />)}
          <div className="field flex align-items-center gap-2">
            <input type="checkbox" id="konkr-featured" checked={form.featured} disabled={disabled}
              onChange={event => change('featured', event.target.checked)} /><label htmlFor="konkr-featured">Featured</label>
          </div>
        </> : field('awinPartnershipStatus', 'Awin partnership status', <Dropdown inputId="konkr-awinPartnershipStatus"
          options={PARTNERSHIPS.map((status, value) => ({ label: partnershipLabel(status), value }))} value={form.awinPartnershipStatus}
          disabled={disabled} onChange={event => change('awinPartnershipStatus', event.value)} />)}
      </div>
      <KonkrMetadata record={record} />
    </>}
    <Dialog header="Confirm Konkr Change" visible={Boolean(confirmation)} modal draggable={false} style={{ width: '34rem', maxWidth: '95vw' }}
      onHide={() => setConfirmation(null)} footer={<>
        <Button label="Cancel" className="p-button-text" onClick={() => setConfirmation(null)} />
        <Button label={`Confirm ${confirmation ? actionLabel[confirmation.action] : ''}`} onClick={() => perform(confirmation.action)} />
      </>}>
      {confirmation && <div>
        <p>{['reload', 'close'].includes(confirmation.action) ? 'Discard unsaved changes? Reload obtains the current server revision; no changes will be saved.' :
          `${actionLabel[confirmation.action]} this ${isOffer ? 'offer' : 'advertiser'}?${dirty ? ' Unsaved changes will be saved first, then the action will use the returned revision.' : ''}`}</p>
        {(confirmation.action === 'deactivate' || confirmation.removesApproval) &&
          <Message severity="warn" text="This disables the advertiser's published offers. Reactivating or restoring approval will not republish them." />}
        {confirmation.action === 'activate' && <p>Activation does not change Awin approval or automatically republish offers.</p>}
        {confirmation.action === 'publish' && <p>This explicitly publishes the saved offer, subject to backend validation. A future start date means it is not live yet.</p>}
        {confirmation.action === 'draft' && <p>This moves the offer back to Draft. It will no longer be published.</p>}
      </div>}
    </Dialog>
  </Dialog>;
};
export default KonkrEditor;
